/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Bar } from 'react-chartjs-2';
import { Button } from 'react-bootstrap';
import { EMPTY_LIST_PLACEHOLDER } from 'utils/values';
import CountUp from 'react-countup';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import DateRangePicker from 'common/date-range-picker';
import AnalyticsChartContainer, { getDefaultChartOptions } from 'common/analytics-chart-container';
import moment from 'moment';
import axios from 'axios';
import { LMS_BASE_URL } from 'config';
import 'utils/chartSetup';
import {
  MOCK_ENROLLMENT_RESPONSE,
  USE_ANALYTICS_MOCK_DATA,
} from 'analytics/analyticsMockData';
import { Block } from '../components/stats/styled';

const TRACK_DEFINITIONS = {
  'total-honor': 'A free enrollment track where learners can earn a certificate based on an honor code of completion.',
  'total-audit': 'A free track that allows access to course content without graded assignments or a certificate.',
  'total-verified': 'A paid enrollment track where learners are identity-verified to earn an official, '
    + 'shareable certificate.',
  'total-self-paced': 'Courses with no fixed schedule, allowing learners to start and finish at their own speed.',
  'total-instructor-led': 'Courses that follow a specific schedule with fixed start/end dates and deadlines '
    + 'set by the instructor.',
};

const TRACK_COLORS = {
  self_paced_honor: 'rgb(57, 91, 135)',
  self_paced_audit: 'rgb(91, 142, 209)',
  self_paced_verified: 'rgb(163, 182, 238)',
  instructor_led_honor: 'rgb(255, 127, 80)',
  instructor_led_audit: 'rgb(255, 159, 64)',
  instructor_led_verified: 'rgb(255, 205, 86)',
};

const buildEnrollmentGraphState = (data) => {
  const labels = data?.labels || [];
  const enrollmentData = data?.enrollment_data || {};
  const datasets = Object.keys(TRACK_COLORS).map((key) => ({
    label: key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    data: enrollmentData[key] || labels.map(() => 0),
    backgroundColor: TRACK_COLORS[key],
    borderColor: TRACK_COLORS[key],
    borderWidth: 1,
  }));

  return {
    graphData: { labels, datasets },
    summaryTotals: data?.summary_totals || null,
  };
};

const EMPTY_ENROLLMENT_RESPONSE = { labels: [], enrollment_data: {} };

const LearnerEnrollmentGraph = ({ isSuperuser, isStaff }) => {
  const initialState = buildEnrollmentGraphState(
    USE_ANALYTICS_MOCK_DATA ? MOCK_ENROLLMENT_RESPONSE : EMPTY_ENROLLMENT_RESPONSE,
  );
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days'),
    endDate: moment(),
  });
  const [granularity, setGranularity] = useState('days');
  const [graphData, setGraphData] = useState(initialState.graphData);
  const [summaryTotals, setSummaryTotals] = useState(initialState.summaryTotals);

  const applyEnrollmentResponse = (data) => {
    const nextState = buildEnrollmentGraphState(data);
    setGraphData(nextState.graphData);
    setSummaryTotals(nextState.summaryTotals);
  };

  const fetchEnrollmentsData = async (start, end, gran) => {
    if (USE_ANALYTICS_MOCK_DATA) {
      applyEnrollmentResponse(MOCK_ENROLLMENT_RESPONSE);
      return;
    }

    try {
      const { data } = await axios({
        withCredentials: true,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': localStorage.getItem('lms-csrf-token'),
        },
        url: `${LMS_BASE_URL}adminpanel/analytics/enrollment/`,
        params: {
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
          granularity: gran,
        },
      });

      applyEnrollmentResponse(data?.labels?.length ? data : EMPTY_ENROLLMENT_RESPONSE);
    } catch (error) {
      applyEnrollmentResponse(EMPTY_ENROLLMENT_RESPONSE);
    }
  };

  useEffect(() => {
    fetchEnrollmentsData(dateRange.startDate, dateRange.endDate, granularity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, granularity]);

  const handleApply = (event, picker) => {
    setDateRange({
      startDate: picker.startDate,
      endDate: picker.endDate,
    });
  };

  const downloadCSV = () => {
    if (!graphData || !graphData.labels.length) return;

    const timestamp = moment().format('YYYY-MM-DD_HH-mm');
    const header = ['Date Granularity', ...graphData.datasets.map((ds) => ds.label)].join(',');
    const rows = graphData.labels.map((label, index) => [
      label, ...graphData.datasets.map((ds) => ds.data[index]),
    ].join(','));

    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `enrollment_stats_${granularity}_${timestamp}.csv`;
    link.click();
  };

  if (!(isSuperuser || isStaff) && !USE_ANALYTICS_MOCK_DATA) {
    return null;
  }

  if (!USE_ANALYTICS_MOCK_DATA && graphData.labels.length === 0) {
    return null;
  }

  return (
    <div className="analytics-chart-block">
      <h2>Enrollment Stats</h2>

      {summaryTotals && (
        <div className="analytics-stats-row">
          {[
            { id: 'total-honor', label: 'Honor (Free)', value: summaryTotals.honor },
            { id: 'total-audit', label: 'Audit (Free)', value: summaryTotals.audit },
            { id: 'total-verified', label: 'Paid Enrollments', value: summaryTotals.verified },
            { id: 'total-self-paced', label: 'Self Paced', value: summaryTotals.self_paced },
            { id: 'total-instructor-led', label: 'Instructor Led', value: summaryTotals.instructor_led },
          ].map((stat) => (
            <Block key={stat.id} style={{ textAlign: 'center' }}>
              <span className="numbers">
                {stat.value > 0 ? (
                  <CountUp end={stat.value} separator="," />
                ) : (
                  EMPTY_LIST_PLACEHOLDER
                )}
              </span>
              <span className="text-info" style={{ display: 'block', marginTop: '5px' }}>
                {stat.label}
                <OverlayTrigger
                  placement="bottom"
                  overlay={(
                    <Tooltip id={`tooltip-${stat.id}`}>
                      {TRACK_DEFINITIONS[stat.id]}
                    </Tooltip>
                  )}
                >
                  <i className="icon-Help-Outline" style={{ marginLeft: '5px' }}>
                    <span className="sr-only">help</span>
                  </i>
                </OverlayTrigger>
              </span>
            </Block>
          ))}
        </div>
      )}

      <div className="analytics-section">
        <div className="analytics-chart-controls">
          <div>
            <label><strong>Date Range:</strong></label>
            <DateRangePicker
              initialSettings={{
                startDate: dateRange.startDate.toDate(),
                endDate: dateRange.endDate.toDate(),
                maxDate: moment().toDate(),
                ranges: {
                  Today: [moment(), moment()],
                  Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                  'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                  'Last 14 Days': [moment().subtract(13, 'days'), moment()],
                  'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                  'This Month': [moment().startOf('month'), moment().endOf('month')],
                  'Last Month': [
                    moment().subtract(1, 'month').startOf('month'),
                    moment().subtract(1, 'month').endOf('month'),
                  ],
                  'Last 3 Months': [moment().subtract(3, 'month'), moment()],
                  'Last 6 Months': [moment().subtract(6, 'month'), moment()],
                  'Last Year': [moment().subtract(1, 'year'), moment()],
                },
              }}
              onApply={handleApply}
            >
              <input
                type="text"
                className="form-control"
                value={
                  `${dateRange.startDate.format('MM/DD/YYYY')} - ${dateRange.endDate.format('MM/DD/YYYY')}`
                }
                readOnly
              />
            </DateRangePicker>
          </div>

          <div>
            <label><strong>Granularity:</strong></label>
            <select className="form-control" value={granularity} onChange={(e) => setGranularity(e.target.value)}>
              <option value="days">Days</option>
              <option value="months">Months</option>
              <option value="quarters">Quarters</option>
            </select>
          </div>

          <Button variant="primary" onClick={downloadCSV} disabled={!graphData?.labels?.length}>
            <span className="icon-Download-Outline-Color icon" /> Download Report
          </Button>
        </div>

        <AnalyticsChartContainer>
          {({ width, height }) => (
            <Bar
              key={`${Math.round(width)}x${Math.round(height)}`}
              width={width}
              height={height}
              data={graphData}
              options={getDefaultChartOptions({
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { boxWidth: 12, padding: 15 },
                  },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                  },
                },
                scales: {
                  x: { stacked: true },
                  y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: { precision: 0 },
                    title: { display: true, text: 'Enrollments' },
                  },
                },
              })}
            />
          )}
        </AnalyticsChartContainer>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    isSuperuser: state.LOGGEDINUSER.isSuperuser,
    isStaff: state.LOGGEDINUSER.isStaff,
  };
}

export default connect(mapStateToProps)(LearnerEnrollmentGraph);
